/**
 * §8.7 Knowledge Base — predictive search mock + 7 categories with
 * top-article previews.
 */

import React from 'react'
import {
  Bot, LineChart, ServerCog, Layers, CreditCard, AlertTriangle, UserCog,
  Search, Sparkles, ArrowRight,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { KB_CATS } from '../data'
import { TONE } from '../_shared'

const ICONS = { Bot, LineChart, ServerCog, Layers, CreditCard, AlertTriangle, UserCog }

export default function KnowledgeBase() {
  return (
    <section id="kb" className="relative py-24 md:py-28 scroll-mt-24">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Knowledge Base</Eyebrow>
          <SectionHeading align="center"
            title={<>Predictive help centre with{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">the answers already surfaced.</span></>}
            subtitle="Type a question, get the right article — ranked by intent, not keywords. Curated across seven top-level categories."
          />
        </div>

        {/* predictive search preview */}
        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 md:p-5 backdrop-blur-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-60"
            style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(59,130,246,0.10), transparent 70%)' }} />
          <div className="relative flex items-center gap-2 rounded-lg border border-white/10 bg-black/40 px-3 py-2.5">
            <Search className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
            <input readOnly defaultValue="how do I reset a workspace?"
              className="flex-1 bg-transparent text-[12.5px] text-zinc-200 outline-none placeholder-zinc-500" />
            <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-1.5 py-0.5 text-[9.5px] font-mono text-zinc-500">
              <kbd>⌘</kbd><kbd>K</kbd>
            </span>
          </div>
          <div className="relative mt-3 rounded-lg border border-white/10 bg-black/40 p-3">
            <div className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">Predicted match · workspace</div>
            {[
              { l: 'Reset a workspace',             c: 'Workspace' },
              { l: 'Change workspace owner',         c: 'Workspace' },
              { l: 'Migrate branding across tenants', c: 'Workspace' },
            ].map((r, i) => (
              <div key={r.l} className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${i === 0 ? 'bg-white/[0.04] border border-white/10' : ''}`}>
                <Sparkles className="h-3 w-3 text-brand-gold" />
                <span className={`text-[12.5px] ${i === 0 ? 'text-white font-semibold' : 'text-zinc-300'} flex-1`}>{r.l}</span>
                <span className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">{r.c}</span>
              </div>
            ))}
          </div>
        </div>

        {/* categories grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
          {KB_CATS.map((c) => {
            const tone = TONE[c.tone] || TONE.orange
            const Icon = ICONS[c.iconKey] || Sparkles
            return (
              <div key={c.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl transition-all hover:border-white/20 hover:bg-white/[0.03] overflow-hidden flex flex-col gap-3">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />
                <div className="relative flex items-center gap-2">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 15, height: 15 }} />
                  </span>
                  <span className="text-[14px] font-semibold text-white">{c.label}</span>
                </div>
                <ul className="relative flex flex-col gap-1">
                  {c.top.map((t) => (
                    <li key={t} className="flex items-center gap-2 text-[12px] text-zinc-300 hover:text-white transition-colors group cursor-default">
                      <ArrowRight className="h-3 w-3 text-zinc-500 group-hover:text-brand-gold transition-colors shrink-0" />
                      <span className="truncate">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
